// ProductPhotosTab.js
import React, { useEffect, useState } from 'react';
import { Upload, Modal, Row, Col, Typography, Button, Space, Card, notification, message } from 'antd';
import { PlusOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import axiosInstance from '../../../../axiosInterceptor';
import { useConfigQuery } from '../../../../queries/useConfig';

const { Text } = Typography;
const accessToken = window.localStorage.getItem("accessToken");
const styles = {
  btnSave: { backgroundColor: '#000', borderColor: '#000', color: '#fff' },
  btnRemove: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' },
};

const getBase64FromFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

function onlyBase64(dataUrlOrRaw) {
  if (!dataUrlOrRaw) return '';
  const i = dataUrlOrRaw.indexOf(',');
  return i >= 0 ? dataUrlOrRaw.slice(i + 1) : dataUrlOrRaw;
}

export default function ProductPhotosTab({ produto, onSaved }) {
  const { data: config } = useConfigQuery();

  const [slots, setSlots] = useState([[], [], [], []]);
  const [saving, setSaving] = useState([false, false, false, false]); // loading por slot
  const [preview, setPreview] = useState({ open: false, title: '', src: '' });
  const maxCount = 1;

  // Carrega imagens existentes (img1..img4) como URLs de visualização
  useEffect(() => {
    const ids = [produto?.img1, produto?.img2, produto?.img3, produto?.img4];
    const next = [[], [], [], []];

    ids.forEach((id, idx) => {
      const has = id !== null && id !== undefined && String(id).trim() !== '';
      if (has) {
        const url = `https://api.pedtech.com.br/imagens/${id}`;
        next[idx] = [
          {
            uid: `${idx}-${id}`,
            name: `image-${idx + 1}.jpg`,
            status: 'done',
            url,
            __existing: true,
            __imageId: id,
            __dirty: false, // existente e não alterada
          },
        ];
      }
    });

    setSlots(next);
  }, [produto?.img1, produto?.img2, produto?.img3, produto?.img4]);

  const handlePreview = async (file) => {
    let src = file.url || file.thumbUrl;
    if (!src && file.originFileObj) {
      src = await getBase64FromFile(file.originFileObj);
    }
    setPreview({
      open: true,
      title: file.name || (file.url ? file.url.substring(file.url.lastIndexOf('/') + 1) : 'Preview'),
      src,
    });
  };

  const handleCancelPreview = () => setPreview((p) => ({ ...p, open: false }));

  const onChangeSlot = (slotIndex, { fileList }) => {
    // trocou a imagem -> marca como dirty e remove flags de existente
    const trimmed = fileList.slice(-maxCount).map((f) => ({
      ...f,
      __existing: false,
      __imageId: undefined,
      __dirty: true,
    }));

    setSlots((prev) => {
      const copy = [...prev];
      copy[slotIndex] = trimmed;
      return copy;
    });
  };

  // Pedtech: upload base64 via multipart/form-data (campo imagemBase64)
  async function postImageBase64(dataUrlOrRaw) {
    const imagemBase64 = onlyBase64(dataUrlOrRaw);
    const form = new FormData();
    form.append('imagemBase64', imagemBase64);

    const options = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const res = await axios.post(
      'https://api.pedtech.com.br/v1/img/uploadImgProduto',
      form,
      options
    );

    if (res.status >= 400) {
      throw new Error(`HTTP ${res.status}: ${typeof res.data === 'string' ? res.data : JSON.stringify(res.data)}`);
    }
    return res.data; // esperado: { id: <idImagem> }
  }

  // Pedtech: deletar imagem existente
  async function deleteRemoteImage(imageId) {
    const options = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    };
    const tryUrl = await axios.delete(
      `https://api.pedtech.com.br/v1/img/deleteImgProduto/${imageId}`,
      options
    );
    if (tryUrl.status === 200 || tryUrl.status === 204) return true;

    throw new Error(`Falha ao deletar imagem ${imageId}. Status: ${tryUrl.status}`);
  }

  // Seu backend: atualizar (zerar ou setar idImagem)
  async function atualizarImagemNoProduto({ lojaId, produtoId, slotIndex, idImagem }) {
    const options = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    };
    return axiosInstance.put(
      `https://techspdv-api-d3e739785e34.herokuapp.com/v1/restaurantes/${lojaId}/produtos/img/${produtoId}`,
      { numeroImagem: slotIndex + 1, idImagem }, // quando remover, use null
      options
    );
  }

  // Remover slot (apaga remoto se existir + zera no backend)
  const handleRemoveSlot = async (slotIndex) => {
    const lojaId = config?.idLoja;
    const produtoId = produto?.id;
    console.log(lojaId);
    console.log();

    const item = slots[slotIndex]?.[0];
    if (!item) {
      setSlots((prev) => {
        const copy = [...prev];
        copy[slotIndex] = [];
        return copy;
      });
      return;
    }

    try {
      message.loading({ content: `Removendo imagem ${slotIndex + 1}...`, key: `rem${slotIndex}`, duration: 0 });

      const imageId = item.__imageId;
      if (imageId) {
        await deleteRemoteImage(imageId);
      }

      if (lojaId && produtoId) {
        const resp = await atualizarImagemNoProduto({
          lojaId,
          produtoId,
          slotIndex,
          idImagem: null,
        });
        if (!(resp?.status === 200)) {
          throw new Error(`Falha ao atualizar produto no slot ${slotIndex + 1}`);
        }
        onSaved(resp.data);
      }

      setSlots((prev) => {
        const copy = [...prev];
        copy[slotIndex] = [];
        return copy;
      });

      message.destroy(`rem${slotIndex}`);
      notification.success({
        message: 'Imagem removida',
        description: `Imagem ${slotIndex + 1} removida com sucesso.`,
      });
    } catch (err) {
      console.error(err);
      message.destroy(`rem${slotIndex}`);
      notification.error({
        message: 'Erro ao remover',
        description: err?.message || 'Não foi possível remover a imagem.',
      });
    }
  };

  // Salvar APENAS um slot
  const handleSaveSlot = async (slotIndex) => {
    const lojaId = config?.idLoja;
    const produtoId = produto?.id;
    console.log(lojaId);
    console.log(produtoId);
    if (!lojaId || !produtoId) {
      notification.error({
        message: 'Dados insuficientes',
        description: 'Não foi possível identificar loja/produto.',
      });
      return;
    }

    const item = slots[slotIndex]?.[0];
    if (!item) {
      notification.warning({
        message: 'Nada para salvar',
        description: `O slot ${slotIndex + 1} está vazio.`,
      });
      return;
    }
    if (item.__existing && item.__dirty === false) {
      notification.info({
        message: 'Sem alterações',
        description: `A imagem ${slotIndex + 1} não foi modificada.`,
      });
      return;
    }

    try {
      setSaving((prev) => {
        const copy = [...prev];
        copy[slotIndex] = true;
        return copy;
      });
      message.loading({ content: `Salvando imagem ${slotIndex + 1}...`, key: `save${slotIndex}`, duration: 0 });

      let dataUrlBase64;
      if (item.originFileObj) {
        dataUrlBase64 = await getBase64FromFile(item.originFileObj);
      } else if (item.thumbUrl) {
        dataUrlBase64 = item.thumbUrl;
      } else {
        message.destroy(`save${slotIndex}`);
        notification.warning({
          message: 'Ignorado',
          description: 'Não foi possível ler a imagem selecionada.',
        });
        return;
      }

      const uploadResp = await postImageBase64(dataUrlBase64);
      const idImagem = uploadResp?.id;
      if (!idImagem) throw new Error('Upload sem id retornado.');

      const atualizacao = await atualizarImagemNoProduto({
        lojaId,
        produtoId,
        slotIndex,
        idImagem,
      });
      if (atualizacao?.status !== 200) {
        throw new Error('Falha ao vincular imagem no produto.');
      }
      produto = atualizacao.data;

      setSlots((prev) => {
        const copy = [...prev];
        copy[slotIndex] = [
          {
            uid: `${slotIndex}-${idImagem}`,
            name: `image-${slotIndex + 1}.jpg`,
            status: 'done',
            url: `https://api.pedtech.com.br/imagens/${idImagem}`,
            __existing: true,
            __imageId: idImagem,
            __dirty: false,
          },
        ];
        return copy;
      });

      message.destroy(`save${slotIndex}`);
      onSaved(atualizacao.data);
      notification.success({
        message: 'Sucesso',
        description: `Imagem ${slotIndex + 1} salva!`,
      });
    } catch (e) {
      console.error(e);
      message.destroy(`save${slotIndex}`);
      notification.error({
        message: 'Erro ao salvar',
        description: e?.message || 'Falha ao salvar a imagem.',
      });
    } finally {
      setSaving((prev) => {
        const copy = [...prev];
        copy[slotIndex] = false;
        return copy;
      });
    }
  };

  // desabilita "Salvar" quando: vazio | existente e não alterada | salvando
  const isSaveDisabled = (slotIndex) => {
    const item = slots[slotIndex]?.[0];
    if (!item) return true;
    if (item.__existing && item.__dirty === false) return true;
    if (saving[slotIndex]) return true;
    return false;
  };

  const UploadSlot = ({ index, title }) => {
    const fileList = slots[index] || [];
    return (
      <Card
        size="small"
        title={<Text strong>{title}</Text>}
        extra={
          <Space>
            <Button
              size="small"
              icon={<UploadOutlined />}
              onClick={() => handleSaveSlot(index)}
              disabled={isSaveDisabled(index)}
              style={styles.btnSave}
            >
              Salvar
            </Button>
            {fileList.length > 0 ? (
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveSlot(index)}
                disabled={saving[index]}
                style={styles.btnRemove}
              >
                Remover
              </Button>
            ) : null}
          </Space>
        }
        style={{ width: '100%' }}
      >
        <Upload
          accept="image/*"
          listType="picture-card"
          fileList={fileList}
          maxCount={1}
          beforeUpload={() => false}
          onChange={(info) => onChangeSlot(index, info)}
          onPreview={handlePreview}
        >
          {fileList.length >= 1 ? null : (
            <div>
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>Adicionar</div>
            </div>
          )}
        </Upload>
        <Text type="secondary">PNG/JPG, até 5MB.</Text>
      </Card>
    );
  };

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col span={24}>
          <Text strong>Adicione até 4 imagens do produto</Text>
        </Col>

        <Col xs={24} md={12}>
          <UploadSlot index={0} title="Imagem 1" />
        </Col>
        <Col xs={24} md={12}>
          <UploadSlot index={1} title="Imagem 2" />
        </Col>
        <Col xs={24} md={12}>
          <UploadSlot index={2} title="Imagem 3" />
        </Col>
        <Col xs={24} md={12}>
          <UploadSlot index={3} title="Imagem 4" />
        </Col>
      </Row>

      <Modal open={preview.open} title={preview.title} footer={null} onCancel={handleCancelPreview}>
        <img alt="preview" style={{ width: '100%' }} src={preview.src} />
      </Modal>
    </div>
  );
}
